
import {
  ADD_FAVORITE,
  CLICK_PRODUCT_SEARCH,
} from '@/utils/amplitudeEvent/amplitudeEvents.js';
import { getLastClickSection } from '@/utils/localStorageUtils.js';
import { numberToCommaString } from '@/utils/numberString';
import { getPathAt } from '@/utils/path';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import CommonProfile from '@/components/common/CommonProfile';
import ScheduleStatusTag from '@/components/common/badge/ScheduleStatusTag';
import HorizontalThumbItem from '@/components/common/product/HorizontalThumbItem';

export default {
  name: 'HorizontalSearchItem',
  components: {
    CommonProfile,
    ScheduleStatusTag,
    HorizontalThumbItem,
  },
  props: {
    id: { type: Number, required: true },
    title: { type: String, required: true },
    thumbnailImg: { type: String, required: true },
    minAge: { type: Number, required: true },
    maxAge: { type: Number, required: true },
    classType: { type: String, required: true },
    classDifficulty: { type: Array, required: false, default: () => [] },
    classPrice: { type: Number, required: false, default: 0 },
    discountPrice: { type: Number, required: false, default: 0 },
    reviewRating: { type: Number, required: false, default: null },
    ratingCount: { type: Number, required: false, default: 0 },
    // sectionName: { type: String, default: null },
    isFull: { type: Boolean, required: false, default: false },
    hasSchedule: { type: Boolean, required: false, default: true },
    classStatus: { type: String, required: false, default: '' },
    profileImg: { type: String, required: false, default: '' },
    name: { type: String, required: false, default: '' },
    teacherRatingCount: { type: Number, required: false, default: 0 },
    isFromNoFilterSearchList: {
      type: Boolean,
      default: false,
    },
    index: { type: Number, required: true },
    score: { type: Number, required: true },
  },
  data() {
    return {
      isPending: false,
    };
  },
  computed: {
    ...mapGetters('search', ['buttonTypeFromProduct']),
    ...mapGetters('user/product', ['myProductList']),
    isEnrolled: ({ $auth, myProductList, id }) =>
      $auth.loggedIn && id > -1 && myProductList.includes(id),
    classAgeString() {
      return `${this.minAge}~${this.maxAge}세`;
    },
    discountRate() {
      if (this.discountPrice) {
        return Math.floor(
          (this.discountPrice / (this.classPrice + this.discountPrice)) * 100,
        );
      }
      return 0;
    },
    classPriceString() {
      return numberToCommaString(this.classPrice);
    },
    ageText() {
      const age = this.classAge.replace('세', '').split('~');
      if (age.length < 2) return this.classAge;
      return age[0] === age[1] ? `${age[0]}세` : this.classAge;
    },
    ratingData() {
      if (this.ratingCount > 0) {
        return {
          rate: this.reviewRating,
          rateCount: this.ratingCount,
        };
      }

      return { rate: 0, rateCount: 0 };
    },
  },
  methods: {
    ...mapMutations({
      addFavorite: 'user/favorite/ADD_FAVORITE_PRODUCT',
      removeFavorite: 'user/favorite/REMOVE_FAVORITE_PRODUCT',
    }),
    ...mapActions('user/favorite', ['toggleProductFavorite']),
    logEvent() {
      const scheduleStatus = this.isFull
        ? 'sold out'
        : !this.hasSchedule
          ? 'preparing'
          : 'available';
      const from = this.$nuxt.$route.query.from;
      const inputKeyword = this.$nuxt.$route.query.keyword;
      const bestKeyword = from === 'best' ? inputKeyword : '';
      const recommendKeyword = from === 'recommend' ? inputKeyword : '';
      const eventProperties = {
        section: this.sectionName,
        product_id: this.id,
        at: this.isFromNoFilterSearchList
          ? 'empty_search_result'
          : getPathAt(this.$route),
        class_review_cnt: this.ratingCount,
        teacher_review_cnt: this.teacherRatingCount,
        teacher_id: this.teacherId,
        class_type: this.classType,
        product_status: this.classStatus,
        is_enrolled: this.isEnrolled,
        schedule_status: scheduleStatus, // 클릭 당시 스케줄이 있는지 없는지, 전석매진인지 (available|sold out|preparing)
        click_rank: this.index, // 검색결과 내 몇순위에서 클릭했는지
        click_recommend_score: this.score, // 검색결과 내 클릭시 추천지수 몇점이었는지
      }
      if (!bestKeyword && !recommendKeyword) { // 직접 입력한 검색어
        eventProperties.input_keyword = inputKeyword;
      }
      if (bestKeyword) { // 인기 키워드 클릭시
        eventProperties.click_best_keyword = bestKeyword;
      }
      if (recommendKeyword) { // 추천 키워드 클릭시
        eventProperties.click_recommend_keyword = recommendKeyword;
      }
      delete eventProperties.keyword;
      this.logUserCustomEvent(CLICK_PRODUCT_SEARCH, eventProperties);
    },
    async onClickFavoriteButton() {
      if (this.isPending) return;

      try {
        this.isPending = true;

        const res = await this.toggleProductFavorite({ productId: this.id });

        if (res?.isFavorite) {
          // 찜하기 성공시
          this.logFavoriteEvent();

          this.$toast.open({
            message:
              '<p class="font-caption-bold">찜한 수업에 추가되었어요.</p>',
            type: 'default',
            duration: 2000,
          });

          this.addFavorite({
            classId: this.id,
            product: {
              id: this.id,
              thumbnailImg: this.thumbnailImg,
              title: this.title,
              classType: this.classType,
              classAge: this.classAge,
              classPrice: this.classPrice,
              discountRate: this.discountRate,
              ratingCount: this.ratingCount,
              rating: this.rating,
              teacher: this.teacher,
              productStatus: this.classStatus,
              isFull: this.isFull,
              hasSchedule: this.hasSchedule,
            },
          });
        } else {
          this.removeFavorite({ classId: this.id });
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isPending = false;
      }
    },
    logFavoriteEvent() {
      this.logUserCustomEvent(ADD_FAVORITE, {
        at: getPathAt(this.$route),
        product_id: this.id,
        component: 'list',
        section: getLastClickSection(),
        product_status: this.productStatus,
        class_type: this.classType,
      });

      this.setAmplitudeUserPropertiesOnce({
        first_favorite_date: this.getCurrentDateTimeFromKST(),
      });
    },
  },
};
